import type { ReactNode } from "react";

import "./Selo.css";

/**
 * A pílula de estado — e as <b>duas</b> famílias que ela atende.
 *
 * <p>Vive aqui, e não no módulo que a usa, porque a cor de "vencendo" precisa
 * ser <b>a mesma</b> em toda tela do sistema. Estado ambíguo numa interface
 * regulada não é questão de estética: quem lê rápido decide errado.
 *
 * <h2>Situação regulatória ≠ status de material</h2>
 *
 * São perguntas diferentes e não se misturam:
 *
 * <ul>
 *   <li><b>{@link Situacao}</b> — "este documento cobre esta operação?"
 *       É o papel na gaveta: vigente, vencendo, vencido, em obtenção, isento.</li>
 *   <li><b>{@link StatusDeMaterial}</b> — "este lote pode ser usado?"
 *       É a etiqueta física na prateleira: quarentena, aprovado, reprovado,
 *       bloqueado, consumido, vencido.</li>
 * </ul>
 *
 * <p>Um insumo <b>aprovado</b> pode estar amparado por uma habilitação
 * <b>vencendo</b>. As duas informações convivem na mesma tela, e reusar a
 * mesma paleta faria a interface sugerir uma relação que não existe — é o que
 * {@code dominio.css} decidiu, e este componente passou a honrar.
 *
 * <p>Até esta entrega o {@code Selo} só expunha o vocabulário regulatório, e a
 * tela de lotes <b>emprestava</b> os nomes da família errada para chegar à cor
 * certa. Funcionava por coincidência de tom em três dos cinco estados, e
 * apagava a diferença entre {@code BLOQUEADO} e {@code CONSUMIDO}, que saíam
 * iguais. Hoje as duas famílias têm variantes próprias.
 *
 * <h2>Nunca só cor — e nunca só cor E fundo</h2>
 *
 * Sempre <b>fundo pastel + borda + texto</b>. O texto porque iluminação de
 * laboratório é ruim, monitor é antigo e há quem não distinga verde de âmbar.
 * A borda porque os pastéis ficam entre <b>1.08:1 e 1.14:1</b> contra o branco:
 * sem contorno a pílula não tem limite visível — medido, não impressão.
 */

/** Situação regulatória — "este documento cobre esta operação?" */
export type Situacao = "vigente" | "vencendo" | "vencido" | "emObtencao" | "isento";

/**
 * Status de material — "este lote pode ser usado?"
 *
 * <p>O vocabulário é o de {@code SituacaoDoLote} (plantus-api), mais
 * {@code vencido}: a validade vence <b>fora</b> do enum, e um lote aprovado
 * pode vencer amanhã.
 */
export type StatusDeMaterial =
  | "quarentena"
  | "aprovado"
  | "reprovado"
  | "bloqueado"
  | "consumido"
  | "vencido";

/**
 * Estado do ATIVO — "este tambor está em serviço?"
 *
 * <p>A terceira pergunta, e a mais nova (ADR-0012). Ela não decide o uso de um
 * material nem a cobertura de um documento: ela diz se o <b>recipiente</b> —
 * o tambor, a bombona — está apto, suspenso ou fora de uso.
 *
 * <p>O vocabulário é o de {@code EstadoDoRecipiente} (plantus-api). Note o que
 * <b>não</b> está aqui: {@code OCUPADO}, {@code VAZIO_SUJO}, {@code EM_LIMPEZA}.
 * Esses são <b>derivados</b> de outro registro e não viram estado gravado —
 * gravá-los seria a segunda verdade que o ADR-0012 item 5 rejeita.
 */
export type EstadoDeAtivo = "emQualificacao" | "ativo" | "bloqueado" | "aposentado";

const ROTULO_DA_SITUACAO: Record<Situacao, string> = {
  vigente: "Vigente",
  vencendo: "Vencendo",
  vencido: "Vencido",
  emObtencao: "Em obtenção",
  isento: "Isento",
};

const ROTULO_DO_STATUS: Record<StatusDeMaterial, string> = {
  quarentena: "Quarentena",
  aprovado: "Aprovado",
  reprovado: "Reprovado",
  bloqueado: "Bloqueado",
  consumido: "Consumido",
  vencido: "Vencido",
};

/**
 * Uma família ou a outra, nunca as duas.
 *
 * <p>A união discriminada é o controle: {@code <Selo situacao="vigente"
 * status="aprovado">} não compila. Um selo que dissesse as duas coisas ao
 * mesmo tempo teria de escolher uma cor, e a escolha ficaria escondida aqui
 * dentro.
 */
type Props = { children?: ReactNode } & (
  | { situacao: Situacao; status?: undefined; ativo?: undefined }
  | { status: StatusDeMaterial; situacao?: undefined; ativo?: undefined }
  | { ativo: EstadoDeAtivo; situacao?: undefined; status?: undefined }
);

const ROTULO_DO_ATIVO: Record<EstadoDeAtivo, string> = {
  emQualificacao: "Em qualificação",
  ativo: "Ativo",
  bloqueado: "Bloqueado",
  aposentado: "Aposentado",
};

export function Selo({ situacao, status, ativo, children }: Props) {
  if (ativo !== undefined) {
    return (
      <span className={`ui-selo ui-selo--ativo-${ativo}`}>
        {children ?? ROTULO_DO_ATIVO[ativo]}
      </span>
    );
  }

  // A classe de status é prefixada porque "vencido" existe nas DUAS famílias
  // com tokens diferentes — `--status-vencido-fundo` é vermelho-100 e
  // `--situacao-vencido-fundo` é vermelho-50. Um nome de classe só faria as
  // duas colidirem em silêncio, e a última declarada venceria.
  const modificador =
    status !== undefined ? `ui-selo--material-${status}` : `ui-selo--${situacao!}`;

  const rotulo =
    status !== undefined ? ROTULO_DO_STATUS[status] : ROTULO_DA_SITUACAO[situacao!];

  return <span className={`ui-selo ${modificador}`}>{children ?? rotulo}</span>;
}
